// Knowledge base search input with KayQ branding - 110 lines ✅
import { useState, FormEvent } from 'react'
import { Search, Filter } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { D365_VERSIONS, D365_MODULES } from '@/lib/constants'
import { cn } from '@/lib/utils'
import type { SearchFilters } from '@/types/knowledge'
import type { D365Version } from '@/types/database'

interface KBSearchBarProps {
  onSearch: (query: string, filters: SearchFilters) => void
  isLoading?: boolean
}

export function KBSearchBar({ onSearch, isLoading = false }: KBSearchBarProps): JSX.Element {
  const [query, setQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<SearchFilters>({})
  const [validationError, setValidationError] = useState<string | null>(null)

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (query.trim().length < 3) {
      setValidationError('Search query must be at least 3 characters')
      return
    }
    setValidationError(null)
    onSearch(query.trim(), filters)
  }

  const handleQueryChange = (value: string): void => {
    setQuery(value)
    if (validationError && value.trim().length >= 3) {
      setValidationError(null)
    }
  }

  return (
    <Card className="border-kaycoo-teal-light bg-kaycoo-white p-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-kaycoo-charcoal-2" />
            <Input
              type="search"
              placeholder="Search knowledge base... (min 3 characters)"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              className={cn(
                'pl-9 border-kaycoo-white-3 bg-white text-kaycoo-charcoal',
                validationError && 'border-kaycoo-burgundy focus-visible:ring-kaycoo-burgundy'
              )}
              disabled={isLoading}
              aria-label="Search knowledge base"
              aria-invalid={!!validationError}
              aria-describedby={validationError ? 'search-error' : undefined}
            />
            {validationError && (
              <p id="search-error" className="mt-1 text-sm text-kaycoo-burgundy-dark">
                {validationError}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            className="border-kaycoo-teal-dark text-kaycoo-teal-dark hover:bg-kaycoo-teal-pale"
            aria-label="Toggle filters"
            aria-expanded={showFilters}
          >
            <Filter className="h-4 w-4" />
          </Button>
          <Button
            type="submit"
            disabled={isLoading || query.trim().length < 3}
            className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
          >
            {isLoading ? 'Searching...' : 'Search'}
          </Button>
        </div>

        {showFilters && (
          <div className="grid gap-4 border-t border-kaycoo-white-3 pt-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="version-filter" className="text-kaycoo-charcoal">D365 Version</Label>
              <select
                id="version-filter"
                className="flex h-10 w-full rounded-md border border-kaycoo-white-3 bg-white px-3 py-2 text-sm text-kaycoo-charcoal"
                value={filters.d365Version || ''}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    d365Version: e.target.value as D365Version,
                  }))
                }
              >
                <option value="">All Versions</option>
                {D365_VERSIONS.map((version) => (
                  <option key={version} value={version}>
                    {version}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="module-filter" className="text-kaycoo-charcoal">Module</Label>
              <select
                id="module-filter"
                className="flex h-10 w-full rounded-md border border-kaycoo-white-3 bg-white px-3 py-2 text-sm text-kaycoo-charcoal"
                value={filters.modules?.[0] || ''}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    modules: e.target.value ? [e.target.value] : undefined,
                  }))
                }
              >
                <option value="">All Modules</option>
                {D365_MODULES.map((module) => (
                  <option key={module} value={module}>
                    {module}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </form>
    </Card>
  )
}
